import { isDefined } from "@clipboard-health/util-ts";
import { Stack, ThemeProvider } from "@mui/material";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import {
  Illustration,
  PageHeaderWrapper,
  Text,
  Title,
} from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import qs from "query-string";
import { useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { logEvent } from "../../lib/analytics";
import { useLogEffect } from "../../lib/analytics/useLogEffect";
import { ACCOUNTS_BASE_PATH } from "../../redesign/Accounts/paths";
import { Button } from "../../redesign/components/Button";
import { PageHeaderTopRow } from "../../redesign/components/PageHeaderTopRow";
import { PageWrapper } from "../../redesign/components/PageWrapper";
import { PageContent } from "../../redesign/WorkerShifts/components/PageContent";
import { useReferralCode } from "./api/useReferralCode";
import {
  BonusBreakdown,
  ReferralExplanationCard,
  ReferralGuide,
  ReferralPolicy,
  ReferralStats,
} from "./components";
import { WORKPLACE_AFFILIATE_SCREEN_NAME } from "./constants";
import { WORKPLACE_REFERRALS_INVITE_WORKPLACE_MODAL_FULL_PATH } from "./paths";
import { WorkplaceReferralAction, WorkplaceReferralComponent } from "./types";
import { getLatestReferralCode } from "./utils";

const PAGE_PADDING = "20px";

export function WorkplaceReferralsPage() {
  const { showErrorToast } = useToast();
  const worker = useDefinedWorker();
  const location = useLocation();
  const history = useHistory();

  const { data: referralCodeData, isLoading: isLoadingReferralCode } = useReferralCode(
    {
      workerId: worker.userId,
    },
    {
      onError: () => {
        showErrorToast("Something went wrong while fetching the referral code");
      },
    }
  );
  const referralCode = getLatestReferralCode(referralCodeData?.referralCodes ?? []);

  const scrollRef = useRef<HTMLDivElement>(null);

  useLogEffect(APP_V2_USER_EVENTS.WORKPLACE_REFERRAL, {
    action: WorkplaceReferralAction.PAGE_VIEWED,
    component: WorkplaceReferralComponent.WORKPLACE_REFERRAL_PAGE,
    referralCode,
    pageQueryParams: qs.parse(location.search),
  });

  const handleClickBack = () => {
    logEvent(APP_V2_USER_EVENTS.WORKPLACE_REFERRAL, {
      action: WorkplaceReferralAction.BACK_BUTTON_CLICKED,
      component: WorkplaceReferralComponent.WORKPLACE_REFERRAL_PAGE,
      referralCode,
    });
    if (isDefined(location.key)) {
      history.goBack();
    } else {
      history.push(ACCOUNTS_BASE_PATH);
    }
  };

  const handleInviteWorkplace = async () => {
    logEvent(APP_V2_USER_EVENTS.WORKPLACE_REFERRAL, {
      action: WorkplaceReferralAction.INVITE_WORKPLACE_CLICKED,
      component: WorkplaceReferralComponent.WORKPLACE_REFERRAL_PAGE,
      referralCode,
    });

    history.push(
      `${WORKPLACE_REFERRALS_INVITE_WORKPLACE_MODAL_FULL_PATH}?referralCode=${referralCode}`
    );
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper>
        <PageHeaderWrapper
          variant="tertiary"
          sx={(theme) => ({
            backgroundColor: theme.palette.common.white,
          })}
        >
          <PageHeaderTopRow
            title={
              <Title semibold component="h6">
                {WORKPLACE_AFFILIATE_SCREEN_NAME}
              </Title>
            }
            fallbackGoBackPath={ACCOUNTS_BASE_PATH}
            onClickBack={handleClickBack}
          />
        </PageHeaderWrapper>

        <PageContent ref={scrollRef}>
          <Stack direction="column" spacing={4}>
            <Stack
              alignItems="center"
              spacing={6}
              sx={(theme) => ({
                marginLeft: `-${PAGE_PADDING} !important`,
                marginRight: `-${PAGE_PADDING} !important`,
                padding: 6,
                backgroundColor: theme.palette.common.white,
              })}
            >
              <Illustration type="facility" />
              <Text variant="h3" align="center">
                Invite a workplace and earn rewards
              </Text>
              <Text variant="body1" align="center">
                Invite a workplace to join Clipboard and get rewarded once their first shift is
                completed
              </Text>
              <Button
                fullWidth
                disabled={isLoadingReferralCode || !isDefined(referralCode)}
                size="medium"
                onClick={handleInviteWorkplace}
              >
                Invite workplace
              </Button>
            </Stack>

            <BonusBreakdown />

            <ReferralGuide referralCode={referralCode} />

            <ReferralExplanationCard />

            <ReferralStats agentId={worker.userId} />

            <ReferralPolicy referralCode={referralCode} />
          </Stack>
        </PageContent>
      </PageWrapper>
    </ThemeProvider>
  );
}
